/**
 * @fileoverview The farm segment delta WRITER (#37416): what a farm segment after the first stores, and the
 * identifiers a stored delta needs for GraphQL. The READER is makePolicyDetailsFromSegments
 * (farm-policy-utils.ts). Moved here from policy-mod's FarmSegmentManager, unchanged, so the CFM GO
 * importer can store an endorsed term's later versions the same way an endorsement does (#37380).
 */

import { FULL_REPLACE_ARRAY_FIELD_NAMES } from './segment-merge/array-merge-policy';
import {
  isRatingSnapshot,
  isWholeValueField,
  SEGMENT_RATING_SNAPSHOT_FIELD_NAMES
} from './segment-merge/whole-value-policy';
import { FARM_DELTA_DIRECTIVES_FIELD, farmDeltaDirectives } from './segment-merge/delta-directives';

// #31790 / #34355: array fields the positional segment diff must carry in full when they change.
// Declared once beside the reader so the delta writer and reader cannot drift apart (#34355).
const FULL_REPLACE_ARRAY_FIELDS = new Set(FULL_REPLACE_ARRAY_FIELD_NAMES);

/**
 * Non-nullable scalar fields, per farmLiabilityCoverage sub-array, that must be present for the
 * GraphQL response to serialize. Used to backfill delta tombstones — see
 * FarmSegmentManager.ensureLiabilityDeltaIdentifiers (GitHub issue #31476). Keys must match the
 * `String!` / `Int!` fields on the corresponding GraphQL types in staff-schema.graphql.
 *
 * `fallback` is a type-correct empty value used when no real source value is available (id-less
 * tombstones, or an id that can't be matched). It only needs to be non-null and of the right
 * GraphQL type — the value is cosmetic on an `isDeleted` tombstone (delta reconstruction drops the
 * item). Use `0` for `Int!` fields and `''` for `String!` fields.
 */
type LiabilityRequiredField = { name: string; fallback: string | number };
const LIABILITY_DELTA_REQUIRED_FIELDS: Record<string, LiabilityRequiredField[]> = {
  // Id-bearing types — backfilled from the prior segment, matched by id.
  additionalPropertiesExtensions: [{ name: 'type', fallback: '' }], // PropertyLiabilityExtension.type (String!)
  additionalAssetsExtensions: [{ name: 'type', fallback: '' }], // AssetLiabilityExtension.type (String!)
  domesticEmployees: [
    { name: 'firstName', fallback: '' },
    { name: 'lastName', fallback: '' }
  ], // EmployeeDetails
  customFeeding: [
    { name: 'type', fallback: '' },
    { name: 'description', fallback: '' }
  ], // CustomFeedingDetail
  occupancies: [
    { name: 'type', fallback: '' },
    { name: 'description', fallback: '' },
    { name: 'location', fallback: '' }
  ], // OccupancyDetail
  // Id-less types — no id to match on; required fields are filled with type-correct empties.
  customFarmWorkItems: [
    { name: 'type', fallback: '' },
    { name: 'description', fallback: '' }
  ], // FarmWorkDetail
  businessActivities: [
    { name: 'type', fallback: '' },
    { name: 'description', fallback: '' }
  ], // BusinessActivity
  businessPursuits: [{ name: 'type', fallback: 0 }] // BusinessPursuit.type (Int!)
};

/**
 * Backfill __typename, description, and type on farm locations and risks in a delta segment
 * from the full segment details. This ensures GraphQL can resolve the FarmRiskDetail union type
 * when delta segments are returned directly (e.g. after re-rating).
 *
 * Also backfills required scalar fields on farmLiabilityCoverage sub-array tombstones — see
 * ensureLiabilityDeltaIdentifiers (GitHub issue #31476).
 */
export function ensureFarmDeltaIdentifiers(delta: any, fullSegmentDetails: any): void {
  if (delta.farmLocations && Array.isArray(delta.farmLocations)) {
    delta.farmLocations.forEach((location: any) => {
      if (!location) {
        return;
      }

      const existingLocation = fullSegmentDetails.farmLocations?.find((loc: any) => loc?.id === location.id);

      if (!location.__typename) {
        location.__typename = existingLocation?.__typename || '';
      }
      if (!location.description) {
        location.description = existingLocation?.description || '';
      }

      if (location.risks && Array.isArray(location.risks)) {
        location.risks.forEach((risk: any) => {
          if (!risk) {
            return;
          }
          const existingRisk = existingLocation?.risks?.find((r: any) => r?.id === risk.id);

          if (!risk.description) {
            risk.description = existingRisk?.description || '';
          }
          if (!risk.type) {
            risk.type = existingRisk?.type || '';
          }
          if (!risk.__typename) {
            risk.__typename = existingRisk?.__typename || '';
          }
        });
      }
    });
  }

  ensureFarmLiabilityDeltaIdentifiers(delta, fullSegmentDetails);
}

/**
 * Backfill required (non-nullable) scalar fields on farmLiabilityCoverage sub-array items in a
 * delta segment from the full prior segment. GitHub issue #31476.
 *
 * When a liability item is removed (e.g. farm liability toggled off, which empties every
 * sub-array at once), calculateSegmentDelta emits a tombstone `{ id, isDeleted: true }` that
 * omits the item's scalar fields. The GraphQL types for these arrays (PropertyLiabilityExtension,
 * EmployeeDetails, BusinessPursuit, etc.) mark `type`/`description`/`firstName`/`lastName`/
 * `location` as non-nullable and, unlike LocationDetail/FarmRiskDetail, expose no `isDeleted`
 * field — so serializing the returned delta segment fails with "Cannot return null for
 * non-nullable type" errors. Backfilling the required fields makes the response serializable
 * while leaving `isDeleted` intact (ignored by GraphQL, honored by delta reconstruction).
 *
 * Id-bearing types (PropertyLiabilityExtension, EmployeeDetails, etc.) are matched to the full
 * prior segment by `id` and backfilled with their real scalar values. Id-less types
 * (BusinessPursuit, BusinessActivity, FarmWorkDetail) can't be matched: calculateSegmentDelta
 * emits removal tombstones positionally and then `.filter()`s out unchanged entries, so the
 * surviving delta indices no longer line up with the prior segment (e.g. removing one of two
 * businessActivities leaves a single tombstone at delta index 0 that does not correspond to
 * fullArray[0]). Rather than guess a positional source, id-less tombstones — and any id-bearing
 * tombstone whose id can't be found — are filled with type-correct empties (see
 * LIABILITY_DELTA_REQUIRED_FIELDS `fallback`). That value is cosmetic: the item carries
 * `isDeleted: true` and is dropped during delta reconstruction, so we only need a non-null value
 * of the right GraphQL type for the response to serialize.
 */
export function ensureFarmLiabilityDeltaIdentifiers(delta: any, fullSegmentDetails: any): void {
  const deltaCoverage = delta?.farmLiabilityCoverage;
  const fullCoverage = fullSegmentDetails?.farmLiabilityCoverage;
  if (!deltaCoverage || !fullCoverage) {
    return;
  }

  for (const [arrayKey, requiredFields] of Object.entries(LIABILITY_DELTA_REQUIRED_FIELDS)) {
    const deltaArray = deltaCoverage[arrayKey];
    const fullArray = fullCoverage[arrayKey];
    if (!Array.isArray(deltaArray) || !Array.isArray(fullArray)) {
      continue;
    }

    for (const item of deltaArray) {
      if (!item) {
        continue;
      }
      // Only id-bearing items have a reliable source. An unmatched id (or an id-less item) yields
      // `undefined`, and the field is filled from `fallback` below instead of a wrong-positional item.
      const source = item.id != null ? fullArray.find((full: any) => full && full.id === item.id) : undefined;

      for (const { name, fallback } of requiredFields) {
        if (item[name] != null) {
          continue;
        }
        item[name] = source && source[name] != null ? source[name] : fallback;
      }
    }
  }
}

/**
 * Calculate the delta (changes) between two Farm segments.
 *
 * `directives: true` (the CFM GO import, #37380) also records on the delta, as `deltaDirectives`, what the
 * delta cannot express (a cleared value, a removed key, a mid-list insert, a changed id-less list); see
 * segment-merge/delta-directives.ts. Off by default, so policy-mod's endorsement deltas are unchanged.
 */
export function calculateFarmSegmentDelta(
  previousSegment: any,
  currentSegment: any,
  { directives = false }: { directives?: boolean } = {}
): any {
  const getDelta = (prev: any, curr: any): any => {
    if (Array.isArray(prev) && Array.isArray(curr)) {
      // #37481: a list of scalars has no identity to diff by. The positional diff below stored a changed one
      // as `{ ...'x', id: undefined }` = `{ 0: 'x' }`, which no reader can rebuild. Carry the whole current
      // list (the caller only gets here when it changed); makePolicyDetailsFromSegments reads it whole.
      const hasScalar = (arr: any[]): boolean =>
        arr.some((el) => el === null || typeof el !== 'object' || Array.isArray(el));
      if (hasScalar(prev) || hasScalar(curr)) {
        return JSON.stringify(prev) === JSON.stringify(curr) ? undefined : curr;
      }

      // #32576: For id-keyed collections (farmLocations, risks, id-bearing liability items),
      // match items by `id` rather than by array index. The old index-based diff misaligned
      // whenever a non-last item was removed: removing the first of two risks shifted the
      // survivor to index 0, so prev[0] (removed) was compared against curr[0] (survivor) and a
      // tail tombstone was emitted — both entries then carried the SURVIVOR's id. Duplicate ids
      // (a) collide when the delta segment is extracted into policypreview-extended-data
      // (TransactWrite "cannot include multiple operations on one item") and (b) tombstone the
      // wrong entity. Only engage when EVERY element in both arrays is an id-bearing object;
      // id-less arrays (BusinessPursuit/BusinessActivity/FarmWorkDetail) keep the positional
      // path, which downstream code already treats as best-effort (see ensureLiabilityDeltaIdentifiers).
      const allHaveIds = (arr: any[]): boolean =>
        arr.length > 0 &&
        arr.every((el) => el !== null && typeof el === 'object' && !Array.isArray(el) && el.id != null);

      if (allHaveIds(prev) && allHaveIds(curr)) {
        const result: any[] = [];
        const prevById = new Map(prev.map((el) => [el.id, el]));
        const currIds = new Set(curr.map((el) => el.id));

        // Added or modified items, in current order
        for (const currEl of curr) {
          const prevEl = prevById.get(currEl.id);
          if (!prevEl) {
            // Added
            result.push(currEl);
          } else {
            const delta = getDelta(prevEl, currEl);
            if (delta && Object.keys(delta).length > 0) {
              result.push({ ...delta, id: currEl.id });
            }
          }
        }

        // Removed items (present in prev, absent from curr) -> tombstones with the correct id
        for (const prevEl of prev) {
          if (!currIds.has(prevEl.id)) {
            result.push({ id: prevEl.id, isDeleted: true });
          }
        }

        return result;
      }

      const result: any[] = [];
      const maxLength = Math.max(prev.length, curr.length);

      for (let i = 0; i < maxLength; i++) {
        if (i >= curr.length) {
          // Item was removed
          result[i] = { id: prev[i]?.id, isDeleted: true };
        } else if (i >= prev.length) {
          // Item was added
          result[i] = curr[i];
        } else {
          // Item potentially modified
          const delta = getDelta(prev[i], curr[i]);
          if (delta && Object.keys(delta).length > 0) {
            result[i] = { ...delta, id: curr[i].id };
          }
        }
      }
      return result.filter((item) => item !== undefined);
    }

    if (prev && curr && typeof prev === 'object' && typeof curr === 'object') {
      const delta: any = {};
      const allKeys = new Set([...Object.keys(prev), ...Object.keys(curr)]);

      for (const key of allKeys) {
        // A stored segment is parsed JSON, where `__proto__` is an ordinary own key; copying it into the
        // delta would replace the delta's prototype (Aikido on #37416).
        if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
          continue;
        }
        if (!(key in curr)) {
          // Key was removed - we track this in the delta
          delta[key] = undefined;
        } else if (!(key in prev)) {
          // Key was added
          delta[key] = curr[key];
        } else if (JSON.stringify(prev[key]) !== JSON.stringify(curr[key])) {
          // #31790: these arrays hold entries keyed by a non-nullable domain key rather than
          // `id` (scheduleCoverages → `coverageType`; coverageF → category/field) and carry
          // non-nullable schema fields (`coverageType`, `items[].limit`). The generic
          // positional diff below drops every unchanged field, producing a sparse entry that
          // (a) makes mergeArraysByIdOrReplace fall back to "replace entire array" because the
          // key field is missing, and (b) fails GraphQL non-null serialization when the delta
          // segment is returned by previewPolicyChangeAddSegment. Carry the full current array
          // whenever it changes so entries stay complete and the merge keys correctly.
          // A whole-value field (`farmClarionDoorIds`) is one rating's output and can change shape
          // between segments, so it is carried whole too; the reader assigns it verbatim.
          if (FULL_REPLACE_ARRAY_FIELDS.has(key) || isWholeValueField(key)) {
            delta[key] = curr[key];
          } else {
            // Key was modified
            const childDelta = getDelta(prev[key], curr[key]);
            if (childDelta !== undefined) {
              delta[key] = childDelta;
            }
          }
        }
      }

      return Object.keys(delta).length > 0 ? delta : undefined;
    }

    // For primitive values
    return prev !== curr ? curr : undefined;
  };

  const delta = getDelta(previousSegment, currentSegment) || {};

  // The named-insured entity is carried whole whenever any of its fields changes. The preview
  // returns this delta as `segments[n]`, and `EntityDetails.type` is `String!`, so a sparse entity
  // — `{ name }` for a rename, `{}` for a dropped name — failed the whole preview with "Cannot
  // return null for non-nullable type: 'String' within parent 'EntityDetails'" (#37104). updateFarmSegment
  // already replaces the entity wholesale (#34891), so the whole value is also the accurate delta.
  // The reader still deep-merges it, so a field the new entity omits keeps its prior value.
  if (delta.entity !== undefined && currentSegment.entity) {
    delta.entity = structuredClone(currentSegment.entity);
  }

  // A segment's rating outputs are read as that segment's snapshot (SEGMENT_RATING_SNAPSHOT_FIELD_NAMES), so one
  // that changed is carried whole: a diff of it would be read as the whole value and drop the rest. Top level
  // only; a risk's own `coverages` stays a diff. An empty placeholder is not a rating and is left as it was.
  for (const field of SEGMENT_RATING_SNAPSHOT_FIELD_NAMES) {
    const current = currentSegment?.[field];
    if (!isRatingSnapshot(current)) {
      delete delta[field];
    } else if (JSON.stringify(previousSegment?.[field]) !== JSON.stringify(current)) {
      delta[field] = structuredClone(current);
    }
  }

  // Clean up the delta to remove unchanged array items
  if (delta.farmLocations) {
    delta.farmLocations = delta.farmLocations.filter((loc: any) => loc !== null && loc !== undefined);
  }

  // Clean up liability extension arrays in delta
  if (delta.farmLiabilityCoverage?.additionalPropertiesExtensions) {
    delta.farmLiabilityCoverage.additionalPropertiesExtensions =
      delta.farmLiabilityCoverage.additionalPropertiesExtensions.filter((ext: any) => !!ext);
  }
  if (delta.farmLiabilityCoverage?.additionalAssetsExtensions) {
    delta.farmLiabilityCoverage.additionalAssetsExtensions =
      delta.farmLiabilityCoverage.additionalAssetsExtensions.filter((ext: any) => !!ext);
  }

  if (directives) {
    const segmentDirectives = farmDeltaDirectives(previousSegment, currentSegment);
    if (Object.keys(segmentDirectives).length > 0) {
      delta[FARM_DELTA_DIRECTIVES_FIELD] = segmentDirectives;
    }
  }

  return delta;
}
